/**
 * Организация чата (04.10.2026, владелец: «чтобы человек, у которого несколько организаций, мог создавать чаты
 * только для конкретной организации и делать пометку, что этот чат принадлежит именно этой организации»).
 *
 * orgOf(topic) — чья группа: явная запись topic_orgs (карта /api/orgs/topic-map), иначе «Кирпичные беседки»;
 * личка и «Избранное» — ничьи, -1 — личный чат без организации.
 * markOf(topic) — пометка в списке и шапке: только тем, у кого несколько организаций, и не для «своей по
 * умолчанию» — у сотрудника это «Кирпичные беседки» (их чатов большинство, пометка на каждом — шум).
 */
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { ORGS_KEY, TOPIC_ORGS_KEY, fetchMyOrgs, fetchTopicOrgs, type Org } from "@/api/orgs";
import "@/styles/orgs.css";

export interface OrgMark { id: number; name: string }
type TopicLike = { id: number; kind?: string | null };
const APP_PATH: Record<string, string> = {
  reports: "/reports", focal: "/focal", visits_schedule: "/visits", visits_funnel: "/production",
};

export function useTopicOrgs() {
  const orgsQ = useQuery({ queryKey: ORGS_KEY, queryFn: fetchMyOrgs, staleTime: 30_000 });
  const mine = useMemo<Org[]>(() => (orgsQ.data?.orgs ?? []).filter(o => o.status === "member"), [orgsQ.data]);
  const multi = mine.length > 1;
  const mapQ = useQuery({
    queryKey: TOPIC_ORGS_KEY, queryFn: fetchTopicOrgs,
    staleTime: 30_000, refetchInterval: 60_000, enabled: multi || mine.some(o => !!o.account),
  });
  return useMemo(() => {
    const home = mine.find(o => o.is_home) ?? null;
    const byId = new Map(mine.map(o => [o.id, o]));
    const topics = mapQ.data?.topics ?? {};
    const orgOf = (t: TopicLike | null | undefined): OrgMark | null => {
      if (!t || t.kind === "dm" || t.kind === "favorites") return null;
      const raw = topics[String(t.id)];
      if (raw === -1) return null;
      const o = raw != null ? byId.get(raw) : home;
      return o ? { id: o.id, name: o.name } : null;
    };
    const markOf = (t: TopicLike | null | undefined): OrgMark | null => {
      if (!multi) return null;
      const o = orgOf(t);
      return o && !(home && o.id === home.id) ? o : null;
    };
    // Раздел (Контроль сделок, Фокусные, Выезды, Производство) организации-компании brixcrm живёт в её CRM:
    // адрес там, иначе null (04.10.2026). Свои разделы КБ на данных КБ — как были.
    const appUrlOf = (t: TopicLike | null | undefined): string | null => {
      const path = t?.kind ? APP_PATH[t.kind] : undefined;
      if (!t || !path) return null;
      const raw = topics[String(t.id)];
      const o = raw != null && raw !== -1 ? byId.get(raw) : null;
      return o?.account ? `https://${o.account}.brixcrm.ru${path}` : null;
    };
    return { mine, multi, home, orgOf, markOf, appUrlOf };
  }, [mine, multi, mapQ.data]);
}
